import { AlertV2, ButtonV2, Modal } from '@cimplico/components';
import { PlayIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toastNotification } from '../../../../components/toast/Toast';
import { useResumeSubscription } from '../../../../queries/billing/use-resume-subscription';
import { SubscriptionV2 } from '../../../../types/firm';
import PlanConfiguration from './PlanConfiguration';

type PausedAlertProps = {
    subscription: SubscriptionV2;
};

function PausedAlert({ subscription }: PausedAlertProps): JSX.Element | null {
    const [showConfirmation, setShowConfirmation] = useState(false);

    if (subscription.status !== 'PAUSED') return null;

    return (
        <>
            {showConfirmation && <ResumeModal subscription={subscription} onClose={(): void => setShowConfirmation(false)} />}
            <div className="rounded-md border border-yellow-400 bg-yellow-50 w-full p-4 mb-4 flex items-center justify-between">
                <p className="text-sm font-bold text-contentPrimary flex items-center gap-1">
                    Your subscription is currently paused. Please add a payment method below and click the Resume now button to configure
                    you Cimplico Workpapers subscription.
                </p>
                <ButtonV2
                    size="sm"
                    type="secondary"
                    label="Resume now"
                    leftIcon={<PlayIcon className="text-gray-600" />}
                    onClick={(): void => setShowConfirmation(true)}
                />
            </div>
        </>
    );
}

type ResumeModalProps = {
    onClose: () => void;
    subscription: SubscriptionV2;
};
function ResumeModal({ onClose, subscription }: ResumeModalProps): JSX.Element {
    const [activeEntities, setActiveEntities] = useState<number>(subscription.totalActiveEntityAllocation);
    const [interval, setInterval] = useState<string>(subscription.interval);
    const query = useResumeSubscription();
    const [errorMessage, setErrorMessage] = useState('');

    function resume(): void {
        query.mutate(
            { activeEntities, interval },
            {
                onSuccess: () => {
                    onClose();
                    toastNotification('success', 'Your subscription has been resumed.');
                },
                onError: (err) => {
                    const msg = err?.response?.data?.errors?.at(0)?.detail ?? 'Unknown error occurred.';
                    setErrorMessage(msg);
                },
            }
        );
    }

    return (
        <Modal
            isOpen
            onClose={(): void => {
                if (!query.isLoading) {
                    onClose();
                }
            }}
        >
            <div className="p-16 py-8">
                {errorMessage && (
                    <AlertV2
                        type="error"
                        className="mb-4"
                        title="An error occurred while resuming your subscription."
                        description={[errorMessage]}
                        onClose={(): void => {
                            setErrorMessage('');
                        }}
                    ></AlertV2>
                )}
                <h3 className="text-contentPrimary font-bold text-left text-lg">Resume Subscription</h3>
                <p className="text-contentSecondary text-sm max-w-[80ch] text-left">
                    To continue using Cimplico Workpapers, your subscription must be resumed. You may update your plan or billing details
                    prior to resuming.
                    <br />
                    <br />
                    <b>Please note:</b> resuming your subscription will result in immediate billing.
                </p>
                <PlanConfiguration
                    subscription={subscription}
                    interval={interval}
                    activeEntities={activeEntities}
                    setActiveEntities={setActiveEntities}
                    setInterval={setInterval}
                />
                <div className="mt-8 flex flex-col gap-2 w-fit mx-auto">
                    <ButtonV2
                        label="Confirm and Resume"
                        size="sm"
                        isDisabled={query.isLoading}
                        isLoading={query.isLoading}
                        onClick={resume}
                    />
                    <ButtonV2 type="hollow" size="sm" label="Close" isDisabled={query.isLoading} onClick={onClose} />
                </div>
            </div>
        </Modal>
    );
}

export default PausedAlert;
